import { Link } from "react-router-dom";
import { cn } from "@/lib/utils";
import { useTheme } from "@/components/ThemeProvider";

/**
 * Quanta brand lockup — uses the official SVG assets from the production
 * brand kit (`public/brand/logos/*.svg`, `public/brand/icons/*.svg`).
 *
 * variant:
 *   - "light" — for light backgrounds. Uses the navy wordmark
 *     (`quanta-logo-primary.svg` if `withWordmark`, otherwise the symbol).
 *   - "dark"  — for dark backgrounds. Uses the reversed lockup.
 *
 * size:
 *   - "sm"  — 24×24 icon, compact wordmark. Header/nav lockup.
 *   - "md"  — 32×32 icon, mid wordmark. Compact hero.
 *   - "lg"  — 48×48 icon, large wordmark. Auth/login/landing hero.
 *   - "xl"  — 64×64 icon, XL wordmark. Marketing / brand banner.
 *
 * withWordmark:
 *   - true (default) → uses the full logo lockup (Q-mark + "Quanta" wordmark).
 *   - false → symbol-only (just the Q ring + cyan dot).
 *
 * withTagline:
 *   - shows the "Your day, understood." caption below the wordmark.
 *   - Only meaningful when withWordmark=true.
 *
 * Phase 12.3 — When the tenant has set `product_display_name` different
 * from "Quanta" AND has NOT uploaded a custom `logo_primary_url`, we
 * MUST NOT render the default Quanta bitmap next to their name. In
 * that case we render a text-only wordmark using the tenant's
 * `font_family_display` (falling back to the body font). Uploading a
 * custom logo overrides everything.
 */
export function Logo({
  variant = "light",
  size = "sm",
  withWordmark = true,
  withTagline = false,
  className,
  brandName,   // tenant-branded name overrides "Quanta" in alt + wordmark fallback
  logoUrl,     // If set, use this URL instead of the default Quanta SVG lockup.
}) {
  const isDark = variant === "dark";
  const { theme } = useTheme();

  // If tenant is themed to a non-Quanta name AND no logo asset is supplied,
  // render a TEXT-ONLY wordmark. This avoids the ghosted default Quanta
  // bitmap sitting next to a tenant's own name.
  const isThemedNoLogo =
    !!brandName && brandName !== "Quanta" && !logoUrl;

  // Logo asset (official SVG lockup). We use the primary/reversed lockups when
  // we want the icon-and-wordmark combo, and the symbol variants for the
  // icon-only case (e.g. mobile-collapsed sidebar).
  const lockupSrc = logoUrl
    ? (logoUrl.startsWith("http") ? logoUrl
       : logoUrl.startsWith("/") ? `${(process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "")}${logoUrl}`
       : logoUrl)
    : withWordmark
    ? isDark
      ? "/brand/logos/quanta-logo-reversed.svg"
      : "/brand/logos/quanta-logo-primary.svg"
    : isDark
      ? "/brand/logos/quanta-symbol-white-cyan.svg"
      : "/brand/logos/quanta-symbol-navy-cyan.svg";

  const widths = {
    sm: withWordmark ? 128 : 24,
    md: withWordmark ? 168 : 32,
    lg: withWordmark ? 240 : 48,
    xl: withWordmark ? 320 : 64,
  };
  const heights = { sm: 24, md: 32, lg: 48, xl: 64 };
  const w = widths[size] ?? widths.sm;
  const h = heights[size] ?? heights.sm;

  const wordmarkSize = { sm: "text-lg", md: "text-xl", lg: "text-3xl", xl: "text-4xl" }[size] || "text-lg";
  const tagSize = { sm: "text-[10px]", md: "text-xs", lg: "text-sm", xl: "text-sm" }[size] || "text-[10px]";

  // Font family for the themed text-only wordmark. Prefer display font,
  // then body font, then a serviceable fallback.
  const displayFont =
    theme?.font_family_display ||
    theme?.font_family_body ||
    'Inter, ui-sans-serif, system-ui, sans-serif';

  return (
    <Link
      to="/"
      className={cn("inline-flex flex-col leading-none", className)}
      data-testid="brand-logo"
    >
      {isThemedNoLogo ? (
        // Text-only tenant wordmark — no default Quanta bitmap
        <span
          className={cn(
            "font-semibold tracking-tight",
            wordmarkSize,
            isDark ? "text-white" : "text-primary",
          )}
          style={{ fontFamily: displayFont, height: `${h}px`, lineHeight: `${h}px` }}
          data-testid="brand-wordmark-text"
        >
          {brandName}
        </span>
      ) : logoUrl ? (
        <img
          src={lockupSrc}
          alt={brandName || "Quanta"}
          height={h}
          style={{ height: `${h}px`, width: "auto" }}
          draggable="false"
          data-testid="brand-logo-img"
        />
      ) : (
        <img
          src={lockupSrc}
          alt={brandName || "Quanta"}
          width={w}
          height={h}
          style={{ height: `${h}px`, width: "auto" }}
          draggable="false"
          data-testid="brand-logo-img"
        />
      )}
      {withWordmark && withTagline && (
        <span
          className={cn(
            "mt-1.5 font-normal",
            tagSize,
            isDark ? "text-white/60" : "text-q-muted",
          )}
        >
          {theme?.tagline || "Your day, understood."}
        </span>
      )}
    </Link>
  );
}
